import React, { useRef, useEffect, useState } from 'react';
import Toolbar, { Tool } from './Toolbar';
import ColorPalette, { ColorDef } from './ColorPalette';
import ExportModal from './ExportModal';
import { PDFDocument } from 'pdf-lib';
import { cellsToCSV, csvToCells, exportToImage, Cell } from '../utils/export';
import ImageImportModal, { ImportCell } from './ImageImportModal';
import { findNearestYarnName } from '../utils/colornames';

const NUMBER_MARGIN = 40;

function keyFor(x: number, y: number) { return `${x},${y}`; }

type Sel = { x1:number;y1:number;x2:number;y2:number };

export default function GridEditor(){
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const hoverOverlayRef = useRef<HTMLCanvasElement | null>(null);

  const [tool, setTool] = useState<Tool>('pencil');
  // named colors: map id -> {name,hex}
  const [colors, setColors] = useState<Map<string,{name:string,hex:string}>>(()=>{
    const m = new Map<string,{name:string,hex:string}>();
    m.set('c0',{name:'Black', hex:'#000000'});
    return m;
  });
  const [selectedColorId, setSelectedColorId] = useState<string>('c0');
  // color palette helpers
  function addColor(name:string, hex:string){
    // record snapshot (colors + filled) so this action is undoable
    recordHistory(filled);
    const id = `c${Date.now().toString(36)}${Math.floor(Math.random()*1000)}`;
    setColors(prev=>{ const m = new Map(prev); m.set(id,{name,hex}); return m; });
    setSelectedColorId(id);
  }
  function updateColor(id:string, name:string, hex:string){
    // record snapshot so color edits are undoable
    recordHistory(filled);
    setColors(prev=>{ const m = new Map(prev); const cur = m.get(id); if(cur) m.set(id,{name,hex}); return m; });
  }
  function removeColor(id:string){
    // record snapshot so removal is undoable (captures prior colors+filled)
    recordHistory(filled);
    // update colors
    setColors(prev=>{ const m = new Map(prev); m.delete(id); return m; });
    // remove filled cells using a direct set to avoid double-recording history
    setFilled(prev=>{ const m = new Map(prev); for(const [k,v] of Array.from(m.entries())){ if(v === id) m.delete(k); } return m; });
    // pick a fallback selected color from the new colors map
    const remaining = Array.from(colors.keys()).filter(k=>k !== id);
    setSelectedColorId(remaining[0] || 'c0');
  }
  const [width, setWidth] = useState(0);
  const [cellSize, setCellSize] = useState(16);
  const [cellAspect, setCellAspect] = useState<'square'|'tall'|'long'>('square');

  const cellW = (cellAspect === 'long') ? Math.round(cellSize * 1.5) : cellSize;
  const cellH = (cellAspect === 'tall') ? Math.round(cellSize * 1.5) : cellSize;

  // filled cells map
  const [filled, setFilled] = useState<Map<string,string>>(new Map());

  // grid viewport (world coords)
  const [startX, setStartX] = useState(-25);
  const [startY, setStartY] = useState(-25);
  const [cols, setCols] = useState(50);
  const [rows, setRows] = useState(50);

  const [selection, setSelection] = useState<Sel | null>(null);
  const [clipboard, setClipboard] = useState<Cell[] | null>(null);
  const [copyPreview, setCopyPreview] = useState<Cell[] | null>(null);
  const [copyPreviewSingleUse, setCopyPreviewSingleUse] = useState<boolean>(false);
  const [dark, setDark] = useState<boolean>(document.documentElement.classList.contains('dark'));
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [gridBg, setGridBg] = useState<string>(()=>{ const css = getComputedStyle(document.documentElement); return (css.getPropertyValue('--bg') || '#ffffff').trim() || '#ffffff'; });

  // image import modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [processingGlobal, setProcessingGlobal] = useState(false);
  const [toasts, setToasts] = useState<Array<{id:number,text:string}>>([]);
  const toastId = useRef(1);
  function showToast(text: string){ const id = toastId.current++; setToasts(t=>[...t,{id,text}]); window.setTimeout(()=>{ setToasts(t=>t.filter(x=>x.id!==id)); }, 3500); }

  // Load modal state for Replace vs Add & Place decision
  const [loadModalData, setLoadModalData] = useState<{ preview: Cell[]; next: Map<string,string> } | null>(null);

  function handleModalReplace(){ if(!loadModalData) return; replaceFilled(loadModalData.next); setLoadModalData(null); showToast('Pattern loaded (replaced)'); }
  function handleModalAddPlace(){ if(!loadModalData) return; setCopyPreview(loadModalData.preview.length ? loadModalData.preview : null); setCopyPreviewSingleUse(true); setTool('copy'); setLoadModalData(null); showToast('Loaded pattern ready to place; click to paste'); }
  function handleModalCancel(){ setLoadModalData(null); showToast('Load cancelled'); }

  // undo history (stores snapshots of filled maps + colors)
  const historyRef = useRef<Array<{ filled: Map<string,string>, colors: Map<string,{name:string,hex:string}> }>>([]);
  const MAX_HISTORY = 200;
  function recordHistory(prevFilled: Map<string,string>){
    try{ historyRef.current.push({ filled: new Map(prevFilled), colors: new Map(colors) }); }catch(e){}
    if(historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
  }
  function applyFilledChange(mutator: (m: Map<string,string>) => Map<string,string>){
    setFilled(prev => { recordHistory(prev); const copy = new Map(prev); const next = mutator(copy); return next; });
  }
  function replaceFilled(nextMap: Map<string,string>){ setFilled(prev => { recordHistory(prev); return nextMap; }); }
  function undo(){ const h = historyRef.current.pop(); if(h){ setFilled(h.filled); setColors(new Map(h.colors)); } }

  // Save: export current filled cells as CSV and trigger download with `.pattern` extension
  function Save(){
    // build array of cells with hex colors
    const cells: Cell[] = [];
    for(const [k, colorId] of filled.entries()){
      const [xStr, yStr] = k.split(',');
      const x = parseInt(xStr, 10); const y = parseInt(yStr, 10);
      const def = colors.get(colorId);
      const hex = def ? def.hex : '#000000';
      cells.push({ x, y, color: hex });
    }
    const csv = cellsToCSV(cells);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yarngrid.pattern`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast('Pattern saved');
  }

  // Load: accept a File (CSV/.pattern) or open a file picker if none provided.
  // When loading, existing palette hexes are reused; new hexes are added once (no duplicates).
  async function Load(file?: File | null){
    if(!file){
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pattern,.csv,text/csv,text/plain';
      input.onchange = () => { if(input.files && input.files[0]) Load(input.files[0]); };
      input.click();
      return;
    }
    try{
      const text = await file.text();
      const parsed = csvToCells(text);
      // collect unique hex colors from file (normalized uppercase)
      const uniqueHexes = new Set<string>();
      for(const c of parsed) uniqueHexes.add((c.color || '').toUpperCase());

      // build existing hex->id map
      const existingHexToId = new Map<string,string>();
      for(const [id, def] of colors.entries()) existingHexToId.set(def.hex.toUpperCase(), id);

      // prepare mapping hex->palette id; add missing colors once
      const hexToId = new Map<string,string>();
      // record history once before mutating colors
      recordHistory(filled);
      setColors(prev => {
        const m = new Map(prev);
        for(const hexRaw of Array.from(uniqueHexes)){
          const hex = hexRaw || '#000000';
          if(existingHexToId.has(hex)){
            hexToId.set(hex, existingHexToId.get(hex)!);
            continue;
          }
          // check current map (may have been added earlier in this loop)
          const already = Array.from(m.entries()).find(([_,d]) => d.hex.toUpperCase() === hex);
          if(already){ hexToId.set(hex, already[0]); continue; }
          // create new id and insert
          const id = `c${Date.now().toString(36)}${Math.floor(Math.random()*1000)}`;
          const name = findNearestYarnName(hex) || 'Imported';
          m.set(id, { name, hex });
          hexToId.set(hex, id);
        }
        return m;
      });

      // build mapped preview cells (palette ids) and a replaced filled map
      const previewCells: Cell[] = parsed.map(c=>({ x: c.x, y: c.y, color: hexToId.get((c.color||'').toUpperCase()) || existingHexToId.get((c.color||'').toUpperCase()) || 'c0' }));
      const next = new Map<string,string>();
      for(const p of previewCells) next.set(keyFor(p.x, p.y), p.color);

      // If there's existing content, open the in-app modal to ask whether to replace or add/place
      if(filled.size > 0){
        setLoadModalData({ preview: previewCells, next });
        return;
      } else {
        // no existing content — replace directly
        replaceFilled(next);
        showToast('Pattern loaded');
      }
    }catch(e){
      console.error('Failed to load pattern', e);
      showToast('Failed to load pattern');
    }
  }

  // expose Save/Load for quick console access during development
  useEffect(()=>{
    (window as any).Save = Save;
    (window as any).Load = Load;
    return ()=>{ try{ delete (window as any).Save; delete (window as any).Load; }catch(e){} };
  },[filled,colors]);

  const draggingRef = useRef(false);
  const dragStartRef = useRef<{sx:number,sy:number,sel?:Sel,mode:'move'|'select'|'paint'|null} | null>(null);
  const panRef = useRef<{startClientX:number,startClientY:number,origStartX:number,origStartY:number} | null>(null);

  // helper to ensure grid contains blank padding around filled area
  function ensurePaddingAroundFilled(m: Map<string,string>){
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    m.forEach((v,k)=>{ const [x,y] = k.split(',').map(s=>parseInt(s,10)); if(x<minX)minX=x; if(y<minY)minY=y; if(x>maxX)maxX=x; if(y>maxY)maxY=y; });
    if(minX===Infinity) return; // no filled
    // need at least one blank row/col around
    let changed=false;
    // Only expand when filled touches the visible edges to avoid automatic panning
    if(minX === startX) { setStartX(s=>s-1); setCols(c=>c+1); changed=true; }
    if(minY === startY) { setStartY(s=>s-1); setRows(r=>r+1); changed=true; }
    if(maxX === startX + cols -1) { setCols(c=>c+1); changed=true; }
    if(maxY === startY + rows -1) { setRows(r=>r+1); changed=true; }
    return changed;
  }

  // draw grid and cells
  const draw = () => {
    const canvas = canvasRef.current; if(!canvas) return;
    const ctx = canvas.getContext('2d'); if(!ctx) return;
    const w = canvas.width; const h = canvas.height;
    // derive background from local state to avoid affecting global UI
    const bg = gridBg || '#ffffff';
    const css = getComputedStyle(document.documentElement);
    const muted = (css.getPropertyValue('--muted') || '#eeeeee').trim() || '#eeeeee';
    const fg = (css.getPropertyValue('--fg') || '#000000').trim() || '#000000';

    // background (override visually for dark mode without changing stored gridBg)
    ctx.fillStyle = dark ? 'rgb(40,40,40)' : bg;
    ctx.fillRect(0,0,w,h);
    // draw numbers area background with a fade outward toward the palette/toolbar edges
    function cssColorToRgba(col: string, a: number){
      const s = (col || '').trim();
      if(!s) return `rgba(0,0,0,${a})`;
      if(s.startsWith('rgb')){
        const nums = s.match(/\d+\.?\d*/g);
        if(nums && nums.length >= 3) return `rgba(${nums[0]},${nums[1]},${nums[2]},${a})`;
      }
      if(s.startsWith('#')){
        const hex = s.replace('#','');
        const v = hex.length === 3 ? hex.split('').map(c=>c+c).join('') : hex;
        const r = parseInt(v.slice(0,2),16), g = parseInt(v.slice(2,4),16), b = parseInt(v.slice(4,6),16);
        return `rgba(${r},${g},${b},${a})`;
      }
      return s;
    }
    const mutedRgba = cssColorToRgba(muted, 1);
    const mutedTransparent = cssColorToRgba(muted, 0);
    // Draw solid muted areas for the left and top margins, then 'erase' outward with destination-out gradients
    ctx.fillStyle = mutedRgba;
    ctx.fillRect(0,0,NUMBER_MARGIN,h);
    ctx.fillRect(0,0,w,NUMBER_MARGIN);
    // carve a fade from the outer edge inward using destination-out so overlapping fades don't add
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    // left fade: opaque at outer edge -> transparent near grid
    const leftFade = ctx.createLinearGradient(0,0,NUMBER_MARGIN,0);
    leftFade.addColorStop(0, 'rgba(0,0,0,1)');
    leftFade.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = leftFade;
    ctx.fillRect(0,0,NUMBER_MARGIN,h);
    // top fade: opaque at outer edge -> transparent near grid
    const topFade = ctx.createLinearGradient(0,0,0,NUMBER_MARGIN);
    topFade.addColorStop(0, 'rgba(0,0,0,1)');
    topFade.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = topFade;
    ctx.fillRect(0,0,w,NUMBER_MARGIN);
    ctx.restore();

    // grid offset (top-left of world grid area inside canvas)
    const offsetX = NUMBER_MARGIN;
    const offsetY = NUMBER_MARGIN;

    // compute how many grid lines we need to draw to fill the entire canvas (use cellW/cellH)
    const visibleCols = Math.max(cols, Math.ceil((w - offsetX) / cellW));
    const visibleRows = Math.max(rows, Math.ceil((h - offsetY) / cellH));

    // draw grid lines (adjust color for dark mode) if enabled
    if(showGrid){
      const gridStroke = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
      ctx.strokeStyle = gridStroke;
      ctx.lineWidth = 1;
      for(let c=0;c<=visibleCols;c++){
        const x = offsetX + c * cellW + 0.5;
        ctx.beginPath(); ctx.moveTo(x, offsetY); ctx.lineTo(x, offsetY + visibleRows * cellH); ctx.stroke();
      }
      for(let r=0;r<=visibleRows;r++){
        const y = offsetY + r * cellH + 0.5;
        ctx.beginPath(); ctx.moveTo(offsetX, y); ctx.lineTo(offsetX + visibleCols * cellW, y); ctx.stroke();
      }
    }

    // fill cells (value is colorId) — iterate only visible area (much faster when world is large)
    for(let c = 0; c < visibleCols; c++){
      for(let r = 0; r < visibleRows; r++){
        const worldX = startX + c;
        const worldY = startY + r;
        const k = keyFor(worldX, worldY);
        const colorId = filled.get(k);
        if(!colorId) continue;
        const def = colors.get(colorId);
        ctx.fillStyle = def ? def.hex : '#000000';
        if(showGrid){
          const cx = offsetX + c * cellW + 1;
          const cy = offsetY + r * cellH + 1;
          ctx.fillRect(cx, cy, cellW - 2, cellH - 2);
        } else {
          const cx = offsetX + c * cellW;
          const cy = offsetY + r * cellH;
          ctx.fillRect(cx, cy, cellW, cellH);
        }
      }
    }

    // draw numbers for the visible grid (use world coords based on startX/startY)
    ctx.fillStyle = fg;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // draw column numbers rotated like the export and pulled closer to the grid
    const colPull = NUMBER_MARGIN * 0.75;
    // choose labeling step so numbers don't overlap when zoomed out
    const minSpacing = 15; // pixels between labels
    const requiredStep = Math.max(1, Math.ceil(minSpacing / cellW));
    const niceOptions = [1,2,5,10,20,50,100,200,500];
    let step = niceOptions.find(n=>n>=requiredStep) || requiredStep;
    // determine world-aligned first label
    const worldStart = startX;
    const worldEnd = startX + visibleCols - 1;
    const firstLabel = Math.ceil(worldStart / step) * step;
    for(let val = firstLabel; val <= worldEnd; val += step){
      const c = val - startX;
      const cxPos = offsetX + c * cellW + cellW/2;
      const cyPos = colPull;
      ctx.save();
      ctx.translate(cxPos, cyPos);
      ctx.rotate(Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(String(val), 0, 0);
      ctx.restore();
    }
    ctx.textAlign = 'right';
    // rows: use same step so density matches columns
    const rowFirst = Math.ceil(startY / step) * step;
    for(let val = rowFirst; val <= startY + visibleRows - 1; val += step){
      const r = val - startY;
      const y = offsetY + r * cellH + cellH/2;
      ctx.fillText(String(val), NUMBER_MARGIN * 0.75, y);
    }

    // draw persistent selection (on main canvas) when appropriate
    if(selection && !(tool === 'pencil' || tool === 'brush' || tool === 'eraser') && !copyPreview){
      const x1 = Math.min(selection.x1, selection.x2);
      const y1 = Math.min(selection.y1, selection.y2);
      const wCells = Math.abs(selection.x2 - selection.x1) + 1;
      const hCells = Math.abs(selection.y2 - selection.y1) + 1;
      const ox = offsetX + (x1 - startX) * cellW;
      const oy = offsetY + (y1 - startY) * cellH;
      ctx.save();
      ctx.strokeStyle = '#2196f3'; ctx.lineWidth = 2; ctx.setLineDash([4,4]);
      ctx.strokeRect(ox + 1, oy + 1, wCells * cellW - 2, hCells * cellH - 2);
      ctx.restore();
    }
  };

  // resize canvas to container
  useEffect(()=>{
    const el = containerRef.current; if(!el) return;
    const canvas = canvasRef.current; if(!canvas) return;
    function resize(){
      const rect = el!.getBoundingClientRect();
      // account for devicePixelRatio for crisp drawing
      const dpr = window.devicePixelRatio || 1;
      canvas!.width = Math.max(200, Math.floor(rect.width * dpr));
      canvas!.height = Math.max(200, Math.floor(rect.height * dpr));
      canvas!.style.width = `${Math.floor(rect.width)}px`;
      canvas!.style.height = `${Math.floor(rect.height)}px`;
      const ctx = canvas!.getContext('2d'); if(ctx) ctx.setTransform(dpr,0,0,dpr,0,0);
      // overlay size match
      const overlay = overlayRef.current;
      if(overlay){ overlay.width = canvas!.width; overlay.height = canvas!.height; overlay.style.width = canvas!.style.width; overlay.style.height = canvas!.style.height; const octx = overlay.getContext('2d'); if(octx) octx.setTransform(dpr,0,0,dpr,0,0); }
      const hoverOverlay = hoverOverlayRef.current;
      if(hoverOverlay){ hoverOverlay.width = canvas!.width; hoverOverlay.height = canvas!.height; hoverOverlay.style.width = canvas!.style.width; hoverOverlay.style.height = canvas!.style.height; const hctx = hoverOverlay.getContext('2d'); if(hctx) hctx.setTransform(dpr,0,0,dpr,0,0); }
      draw();
    }
    resize();
    window.addEventListener('resize', resize);
    return ()=> window.removeEventListener('resize', resize);
  },[cols,rows,cellSize,cellAspect,filled,startX,startY]);

  // keep draw updated when filled changes
  useEffect(()=>{ draw(); },[filled,cols,rows,startX,startY,cellSize,cellAspect,colors,dark,gridBg,selection,tool,copyPreview, showGrid]);

  // hovered palette color id -> outline matching filled pixels
  const [hoveredColorId, setHoveredColorId] = useState<string | null>(null);

  // draw outlines for hovered color on a separate overlay so other overlays don't interfere
  useEffect(()=>{
    const hoverCanvas = hoverOverlayRef.current; const canvas = canvasRef.current; if(!canvas || !hoverCanvas) return;
    const ctx = hoverCanvas.getContext('2d'); if(!ctx) return;
    // clear
    ctx.clearRect(0,0,hoverCanvas.width, hoverCanvas.height);
    if(!hoveredColorId) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr,0,0,dpr,0,0);
    // outline style: use the color hex but draw a stronger two-pass outline for visibility
    const def = colors.get(hoveredColorId);
    const hex = def ? def.hex : '#000000';
    function hexToRgb(h: string){ const s = h.replace('#',''); const v = s.length===3 ? s.split('').map(c=>c+c).join('') : s; return [parseInt(v.slice(0,2),16), parseInt(v.slice(2,4),16), parseInt(v.slice(4,6),16)]; }
    const [hr, hg, hb] = hexToRgb(hex);
    const colorInner = `rgba(${hr},${hg},${hb},0.95)`;
    const colorGlow = `rgba(0,0,0,0.45)`;
    const offsetX = NUMBER_MARGIN; const offsetY = NUMBER_MARGIN;
    const visibleCols = Math.max(cols, Math.ceil((canvas.width / dpr - offsetX) / cellW));
    const visibleRows = Math.max(rows, Math.ceil((canvas.height / dpr - offsetY) / cellH));
    // iterate visible area rather than all filled entries
    for(let c=0;c<visibleCols;c++){
      for(let r=0;r<visibleRows;r++){
        const wx = startX + c; const wy = startY + r;
        const id = filled.get(keyFor(wx,wy));
        if(id !== hoveredColorId) continue;
        const cx = offsetX + c * cellW + 0.5;
        const cy = offsetY + r * cellH + 0.5;
        // outer glow (dark) for contrast
        ctx.lineWidth = Math.max(2, Math.floor(Math.max(cellW, cellH) * 0.25));
        ctx.strokeStyle = colorGlow;
        ctx.strokeRect(cx - 0.5, cy - 0.5, cellW + 1, cellH + 1);
        // inner colored stroke
        ctx.lineWidth = Math.max(1, Math.floor(Math.max(cellW, cellH) * 0.12));
        ctx.strokeStyle = colorInner;
        ctx.strokeRect(cx, cy, cellW - 1, cellH - 1);
      }
    }
  },[hoveredColorId, filled, startX, startY, cellSize, cellAspect, colors, cols, rows]);

  // interactions: convert client coords to grid cell
  function clientToCell(cx:number, cy:number){
    const canvas = canvasRef.current; if(!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const container = containerRef.current;
    const scrollLeft = container ? container.scrollLeft || 0 : 0;
    const scrollTop = container ? container.scrollTop || 0 : 0;
    const x = cx - rect.left + scrollLeft - NUMBER_MARGIN;
    const y = cy - rect.top + scrollTop - NUMBER_MARGIN;
    const col = Math.floor(x / cellW) + startX;
    const row = Math.floor(y / cellH) + startY;
    return {col,row};
  }

  // helper: parse hex => [r,g,b]
  function rgbFromHex(hex: string){
    const s = hex.replace('#',''); const v = s.length === 3 ? s.split('').map(c=>c+c).join('') : s;
    return [parseInt(v.slice(0,2),16), parseInt(v.slice(2,4),16), parseInt(v.slice(4,6),16)];
  }

  // compute overall content bounds (used for export defaults)
  let _minX = Infinity, _minY = Infinity, _maxX = -Infinity, _maxY = -Infinity;
  filled.forEach((v,k)=>{ const [x,y] = k.split(',').map(s=>parseInt(s,10)); if(x < _minX) _minX = x; if(y < _minY) _minY = y; if(x > _maxX) _maxX = x; if(y > _maxY) _maxY = y; });
  let totalWidthCells = 0, totalHeightCells = 0;
  if(_minX === Infinity){ totalWidthCells = cols; totalHeightCells = rows; }
  else { totalWidthCells = _maxX - _minX + 1; totalHeightCells = _maxY - _minY + 1; }

  function rgbToHex(r:number,g:number,b:number){
    return `#${((1<<24) + (r<<16) + (g<<8) + b).toString(16).slice(1).toUpperCase()}`;
  }

  // helper: find palette id nearest to given hex color (by Euclidean RGB)
  function findNearestPaletteId(hex: string){
    let bestId: string | null = null; let bestD = Infinity;
    const [r0,g0,b0] = rgbFromHex(hex);
    for(const [id,def] of colors.entries()){
      const [r,g,b] = rgbFromHex(def.hex);
      const d = (r - r0)*(r - r0) + (g - g0)*(g - g0) + (b - b0)*(b - b0);
      if(d < bestD){ bestD = d; bestId = id; }
    }
    return bestId;
  }

  // painting/erasing
  function applyBrushAt(cx:number,cy:number, shift=false){
    const pos = clientToCell(cx,cy); if(!pos) return;
    // pencil is a special-case: always single cell. If Shift held, delete that single cell.
    if(tool === 'pencil'){
      const k = keyFor(pos.col, pos.row);
      applyFilledChange(m=>{ if(shift) m.delete(k); else m.set(k, selectedColorId); ensurePaddingAroundFilled(m); return m; });
      return;
    }

    // brush/eraser: compute whether we are erasing. Shift toggles the behavior.
    const r = Math.max(0, width);
    let isEraser = (tool === 'eraser');
    if(shift) isEraser = !isEraser;
    applyFilledChange(m=>{
      for(let dx=-r;dx<=r;dx++) for(let dy=-r;dy<=r;dy++){
        if(dx*dx + dy*dy > r*r) continue;
        const x = pos.col + dx; const y = pos.row + dy;
        const k = keyFor(x,y);
        if(isEraser) m.delete(k); else m.set(k, selectedColorId);
      }
      ensurePaddingAroundFilled(m);
      return m;
    });
  }

  // mouse handlers
  useEffect(()=>{
    const canvas = canvasRef.current; if(!canvas) return;
    function down(e:MouseEvent){
      // right-click starts viewport panning
      if((e as MouseEvent).button === 2){
        (e as MouseEvent).preventDefault();
        panRef.current = { startClientX: (e as MouseEvent).clientX, startClientY: (e as MouseEvent).clientY, origStartX: startX, origStartY: startY };
        return;
      }
      const pos = clientToCell(e.clientX,e.clientY); if(!pos) return;
      // Eyedropper tool: sample color at clicked cell or canvas pixel and set selected color
      if(tool === 'eyedropper'){
        try{
          const k = keyFor(pos.col, pos.row);
          const pid = filled.get(k);
          if(pid){ setSelectedColorId(pid); setTool('pencil'); return; }
          // sample pixel color from canvas and pick nearest palette color
          const canvasEl = canvasRef.current; if(canvasEl){
            const rect = canvasEl.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1;
            const px = Math.floor((e.clientX - rect.left) * dpr); const py = Math.floor((e.clientY - rect.top) * dpr);
            const ctx = canvasEl.getContext('2d'); if(ctx){
              const data = ctx.getImageData(px, py, 1, 1).data;
              if(data && data[3] !== 0){
                const hex = rgbToHex(data[0], data[1], data[2]);
                // try exact match first
                for(const [id,def] of colors.entries()){ if(def.hex.toLowerCase() === hex.toLowerCase()){ setSelectedColorId(id); setTool('pencil'); return; } }
                const nid = findNearestPaletteId(hex);
                if(nid){ setSelectedColorId(nid); setTool('pencil'); }
              }
            }
          }
        }catch(err){ /* ignore sampling errors */ }
        return;
      }
      
        // eyedropper: Ctrl/Cmd + click when using a painting tool selects color from grid (palette id if present)
        if((e.ctrlKey || e.metaKey) && (tool === 'pencil' || tool === 'brush' || tool === 'fill')){
          try{
            const k = keyFor(pos.col, pos.row);
            const pid = filled.get(k);
            if(pid){ setSelectedColorId(pid); return; }
            // fallback: sample pixel color from canvas and pick nearest palette color
            const canvasEl = canvasRef.current; if(canvasEl){
              const rect = canvasEl.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1;
              const px = Math.floor((e.clientX - rect.left) * dpr); const py = Math.floor((e.clientY - rect.top) * dpr);
              const ctx = canvasEl.getContext('2d'); if(ctx){
                const data = ctx.getImageData(px, py, 1, 1).data;
                if(data && data[3] !== 0){
                  const hex = rgbToHex(data[0], data[1], data[2]);
                  // try exact match first
                  for(const [id,def] of colors.entries()){ if(def.hex.toLowerCase() === hex.toLowerCase()){ setSelectedColorId(id); return; } }
                  const nid = findNearestPaletteId(hex);
                  if(nid) setSelectedColorId(nid);
                }
              }
            }
          }catch(err){ /* ignore sampling errors */ }
          return;
        }
      // special-case: if we have a floating copy preview, only commit it when the active tool is still 'copy'
      if(copyPreview){
        if(tool === 'copy'){
          const anchorCol = pos.col; const anchorRow = pos.row;
          applyFilledChange(m=>{ for(const c of copyPreview) m.set(keyFor(anchorCol + c.x, anchorRow + c.y), c.color); ensurePaddingAroundFilled(m); return m; });
          // select the pasted area so it can be moved immediately
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for(const c of copyPreview){ if(c.x < minX) minX = c.x; if(c.y < minY) minY = c.y; if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
          if(minX !== Infinity){ setSelection({ x1: anchorCol + minX, y1: anchorRow + minY, x2: anchorCol + maxX, y2: anchorRow + maxY }); }
          // if this was a single-use preview (from image import), clear it after paste
          if(copyPreviewSingleUse){
            setCopyPreview(null);
            setCopyPreviewSingleUse(false);
            // after pasting an imported image, switch back to pencil for normal editing
            setTool('pencil');
          }
          draggingRef.current = false;
          dragStartRef.current = null;
          return;
        } else {
          // user switched tools; clear stale floating preview
          setCopyPreview(null);
        }
      }
      draggingRef.current = true;
      // Fill tool: single-click flood fill within visible viewport
      if(tool === 'fill'){
        const seedX = pos.col, seedY = pos.row;
        applyFilledChange(m=>{
          // limit fill to the actual visible canvas area to avoid unbounded fills
          const canvasEl = canvasRef.current;
          let visibleCols = cols;
          let visibleRows = rows;
          if(canvasEl){
            const rect = canvasEl.getBoundingClientRect();
            visibleCols = Math.max(cols, Math.ceil((rect.width - NUMBER_MARGIN) / cellW));
            visibleRows = Math.max(rows, Math.ceil((rect.height - NUMBER_MARGIN) / cellH));
          }
          const minXBound = startX; const maxXBound = startX + visibleCols - 1;
          const minYBound = startY; const maxYBound = startY + visibleRows - 1;
          const startKey = keyFor(seedX, seedY);
          const target = m.has(startKey) ? m.get(startKey) as string : null;
          const replacement = (e.shiftKey ? null : selectedColorId);
          // if nothing to change, bail
          if((target === null && replacement === null) || (target !== null && replacement !== null && target === replacement)) return m;
          const stack: [number,number][] = [[seedX, seedY]];
          const seen = new Set<string>();
          while(stack.length){
            const [x,y] = stack.pop()!;
            if(x < minXBound || x > maxXBound || y < minYBound || y > maxYBound) continue;
            const k = keyFor(x,y);
            if(seen.has(k)) continue;
            seen.add(k);
            const cur = m.has(k) ? m.get(k) as string : null;
            if(cur !== target) continue;
            if(replacement === null) m.delete(k); else m.set(k, replacement);
            stack.push([x+1,y]); stack.push([x-1,y]); stack.push([x,y+1]); stack.push([x,y-1]);
          }
          ensurePaddingAroundFilled(m);
          return m;
        });
        draggingRef.current = false;
        dragStartRef.current = null;
        return;
      }
      if(tool === 'select' || tool === 'move'){
        // begin selection or begin move if clicked inside selection
        // If we're in move tool and there's an existing selection, start a move regardless of click location
        if(tool === 'move' && selection){
          dragStartRef.current = { sx: pos.col, sy: pos.row, sel: { x1: selection.x1, y1: selection.y1, x2: selection.x2, y2: selection.y2 }, mode: 'move' };
        } else if(selection && pos.col >= Math.min(selection.x1,selection.x2) && pos.col <= Math.max(selection.x1,selection.x2) && pos.row >= Math.min(selection.y1,selection.y2) && pos.row <= Math.max(selection.y1,selection.y2)){
          // store a snapshot of the selection so dragging uses a stable source
          dragStartRef.current = { sx: pos.col, sy: pos.row, sel: selection ? { x1: selection.x1, y1: selection.y1, x2: selection.x2, y2: selection.y2 } : undefined, mode: 'move' };
        } else {
          // starting a new selection, clear any previous overlays
          clearOverlay();
          setSelection({x1:pos.col,y1:pos.row,x2:pos.col,y2:pos.row});
          dragStartRef.current = { sx: pos.col, sy: pos.row, mode: 'select' };
        }
      } else {
        dragStartRef.current = { sx: pos.col, sy: pos.row, mode: 'paint' };
        applyBrushAt(e.clientX,e.clientY, e.shiftKey);
      }
    }
    function move(e:MouseEvent){
      // handle right-button pan in progress
      const p = panRef.current;
      if(p){
        const dxPx = e.clientX - p.startClientX;
        const dyPx = e.clientY - p.startClientY;
        const dxCells = Math.round(dxPx / cellW);
        const dyCells = Math.round(dyPx / cellH);
        setStartX(Math.max(-1000000, p.origStartX - dxCells));
        setStartY(Math.max(-1000000, p.origStartY - dyCells));
        // keep overlay/preview updated
        clearOverlay();
        return;
      }
      if(!draggingRef.current) return;
      const pos = clientToCell(e.clientX,e.clientY); if(!pos) return;
      const ds = dragStartRef.current;
      if(!ds) return;
      if(ds.mode === 'select'){
        // If Shift is held, force a square selection (equal width and height)
        if(e.shiftKey){
          const sx = ds.sx; const sy = ds.sy; const tx = pos.col; const ty = pos.row;
          const dx = tx - sx; const dy = ty - sy;
          const adx = Math.abs(dx); const ady = Math.abs(dy);
          const sgnx = dx === 0 ? 1 : (dx > 0 ? 1 : -1);
          const sgny = dy === 0 ? 1 : (dy > 0 ? 1 : -1);
          const size = Math.max(adx, ady);
          const nx = sx + sgnx * size;
          const ny = sy + sgny * size;
          setSelection(s => s ? ({...s, x2: nx, y2: ny}) : ({ x1: sx, y1: sy, x2: nx, y2: ny }));
        } else {
          setSelection(s=> s ? ({...s, x2: pos.col, y2: pos.row}) : ({x1: ds.sx, y1: ds.sy, x2: pos.col, y2: pos.row}));
        }
      } else if(ds.mode === 'move'){
        // draw overlay: we'll render on overlay canvas
        const dx = pos.col - ds.sx; const dy = pos.row - ds.sy;
        drawOverlayMove(dx, dy, ds.sel!);
      } else if(ds.mode === 'paint'){
        applyBrushAt(e.clientX,e.clientY, e.shiftKey);
      }
    }
    function up(e:MouseEvent){
      // finish pan if active
      if(panRef.current){ panRef.current = null; return; }
      if(!draggingRef.current) return;
      draggingRef.current = false;
      const pos = clientToCell(e.clientX,e.clientY);
      const ds = dragStartRef.current;
      dragStartRef.current = null;
      if(!ds) return;
      // clear transient overlays before drawing final selection/move
      clearOverlay();
      if(ds.mode === 'select'){
        if(pos){
          // If Shift held, force square selection
          let newSel;
          if((e as MouseEvent).shiftKey){
            const sx = ds.sx; const sy = ds.sy; const tx = pos.col; const ty = pos.row;
            const dx = tx - sx; const dy = ty - sy;
            const adx = Math.abs(dx); const ady = Math.abs(dy);
            const sgnx = dx === 0 ? 1 : (dx > 0 ? 1 : -1);
            const sgny = dy === 0 ? 1 : (dy > 0 ? 1 : -1);
            const size = Math.max(adx, ady);
            const nx = sx + sgnx * size;
            const ny = sy + sgny * size;
            newSel = { x1: sx, y1: sy, x2: nx, y2: ny };
          } else {
            newSel = { x1: ds.sx, y1: ds.sy, x2: pos.col, y2: pos.row };
          }
          // if the user used the select tool and only selected a single cell, treat it as no selection
          if(tool === 'select' && newSel.x1 === newSel.x2 && newSel.y1 === newSel.y2){
            setSelection(null);
          } else {
            setSelection(newSel);
          }
        }
      } else if(ds.mode === 'move'){
        // use the snapshot stored in ds.sel as the source for the move
        if(!ds.sel) return;
        if(!pos) return;
        const dx = pos.col - ds.sx; const dy = pos.row - ds.sy;
        applyMoveSelectionFrom(ds.sel, dx, dy);
      }
    }
    canvas.addEventListener('mousedown', down);
    // disable context menu on the canvas so right-drag can be used for panning
    function preventCtx(e: Event){ e.preventDefault(); }
    canvas.addEventListener('contextmenu', preventCtx);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return ()=>{ canvas.removeEventListener('mousedown', down); canvas.removeEventListener('contextmenu', preventCtx); window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  },[tool,selection,width,startX,startY,cols,rows,filled,copyPreview,copyPreviewSingleUse, selectedColorId, colors, cellSize, cellAspect]);

  // overlay drawing for moving selection
  function drawOverlayMove(dx:number, dy:number, sel:Sel){
    const overlay = overlayRef.current; const canvas = canvasRef.current; if(!overlay||!canvas) return;
    overlay.width = canvas.width; overlay.height = canvas.height;
    const ctx = overlay.getContext('2d'); if(!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr,0,0,dpr,0,0);
    ctx.clearRect(0,0,overlay.width, overlay.height);
    ctx.strokeStyle = '#2196f3'; ctx.lineWidth = 2;
    const x1 = Math.min(sel.x1, sel.x2);
    const y1 = Math.min(sel.y1, sel.y2);
    const wCells = Math.abs(sel.x2 - sel.x1) + 1;
    const hCells = Math.abs(sel.y2 - sel.y1) + 1;
    // draw ghosted cell contents at offset
    for(let sx=0;sx<wCells;sx++){
      for(let sy=0;sy<hCells;sy++){
        const srcX = x1 + sx;
        const srcY = y1 + sy;
        const k = keyFor(srcX, srcY);
        const colId = filled.get(k);
        if(colId){
          const def = colors.get(colId);
          const hex = def ? def.hex : '#000000';
          const px = NUMBER_MARGIN + (srcX - startX + dx) * cellW + 1;
          const py = NUMBER_MARGIN + (srcY - startY + dy) * cellH + 1;
          ctx.fillStyle = hex;
          ctx.fillRect(px, py, cellW - 2, cellH - 2);
        }
      }
    }
    const ox = NUMBER_MARGIN + (x1 - startX + dx) * cellW;
    const oy = NUMBER_MARGIN + (y1 - startY + dy) * cellH;
    const w = wCells * cellW;
    const h = hCells * cellH;
    ctx.strokeRect(ox + 1, oy + 1, w - 2, h - 2);
  }
  function clearOverlay(){ const overlay = overlayRef.current; if(!overlay) return; const ctx = overlay.getContext('2d'); if(!ctx) return; ctx.clearRect(0,0,overlay.width,overlay.height); }

  // overlay-based selection drawing is no longer used for persistent selection; main canvas draws selection.
  function drawSelectionRect(sel:Sel){
    const overlay = overlayRef.current; const canvas = canvasRef.current; if(!overlay||!canvas) return;
    // keep as a helper for transient visuals (no-op here)
    const ctx = overlay.getContext('2d'); if(!ctx) return; ctx.clearRect(0,0,overlay.width, overlay.height);
  }

  function drawCopyPreview(clientX:number, clientY:number, cells:Cell[]){
    const overlay = overlayRef.current; const canvas = canvasRef.current; if(!overlay||!canvas) return;
    const rect = canvas.getBoundingClientRect(); const container = containerRef.current;
    const ctx = overlay.getContext('2d'); if(!ctx) return;
    overlay.width = canvas.width; overlay.height = canvas.height;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr,0,0,dpr,0,0);
    ctx.clearRect(0,0,overlay.width, overlay.height);
    // compute cell under mouse
    const scrollLeft = container ? container.scrollLeft || 0 : 0;
    const scrollTop = container ? container.scrollTop || 0 : 0;
    const x = clientX - rect.left + scrollLeft - NUMBER_MARGIN; const y = clientY - rect.top + scrollTop - NUMBER_MARGIN;
    const col = Math.floor(x / cellW) + startX; const row = Math.floor(y / cellH) + startY;
    for(const c of cells){
      const cx = NUMBER_MARGIN + (col + c.x - startX) * cellW + 1;
      const cy = NUMBER_MARGIN + (row + c.y - startY) * cellH + 1;
      // allow c.color to be either a hex string or a palette id
      const fillHex = (typeof c.color === 'string' && c.color.startsWith('#')) ? c.color : (colors.get(c.color as string)?.hex || '#000000');
      ctx.fillStyle = fillHex;
      ctx.fillRect(cx, cy, cellW - 2, cellH - 2);
    }
    // draw outline of bounding box (compute max values without spreading large arrays)
    let maxX = -Infinity; let maxY = -Infinity;
    for(const c of cells){ if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
    if(maxX === -Infinity) maxX = 0; if(maxY === -Infinity) maxY = 0;
    const bx = NUMBER_MARGIN + (col - startX) * cellW; const by = NUMBER_MARGIN + (row - startY) * cellH;
    const bw = (maxX + 1) * cellW; const bh = (maxY + 1) * cellH;
    ctx.strokeStyle = 'rgba(33,150,243,0.9)'; ctx.lineWidth = 2; ctx.strokeRect(bx + 1, by + 1, bw - 2, bh - 2);
  }

  // draw a brush-size preview at client coords
  function drawBrushPreview(clientX:number, clientY:number, shift = false){
    const overlay = overlayRef.current; const canvas = canvasRef.current; const container = containerRef.current; if(!overlay||!canvas||!container) return;
    const rect = canvas.getBoundingClientRect();
    const scrollLeft = container.scrollLeft || 0;
    const scrollTop = container.scrollTop || 0;
    const xInContent = clientX - rect.left + scrollLeft - NUMBER_MARGIN;
    const yInContent = clientY - rect.top + scrollTop - NUMBER_MARGIN;
    const col = Math.floor(xInContent / cellW) + startX;
    const row = Math.floor(yInContent / cellH) + startY;

    const ctx = overlay.getContext('2d'); if(!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr,0,0,dpr,0,0);
    // clear previous preview but preserve selection move overlay if active
    ctx.clearRect(0,0,overlay.width, overlay.height);

    // pencil preview should always be a single cell square. If Shift is held, show it as eraser (red).
    if(tool === 'pencil'){
      const cx = NUMBER_MARGIN + (col - startX + 0.5) * cellW;
      const cy = NUMBER_MARGIN + (row - startY + 0.5) * cellH;
      const sW = cellW - 2;
      const sH = cellH - 2;
      if(shift){ ctx.fillStyle = 'rgba(226,80,65,0.12)'; ctx.strokeStyle = 'rgba(226,80,65,0.9)'; }
      else { ctx.fillStyle = 'rgba(33,150,243,0.12)'; ctx.strokeStyle = 'rgba(33,150,243,0.7)'; }
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.rect(cx - sW/2 + 0.5, cy - sH/2 + 0.5, sW, sH); ctx.fill(); ctx.stroke();
      return;
    }

    // circular preview for brush/eraser
    const sizeCells = (2 * Math.max(0,width) + 1);
    const cx = NUMBER_MARGIN + (col - startX + 0.5) * cellW;
    const cy = NUMBER_MARGIN + (row - startY + 0.5) * cellH;
    const rPxX = (sizeCells * cellW) / 2;
    const rPxY = (sizeCells * cellH) / 2;
    if(shift){ ctx.strokeStyle = 'rgba(226, 80, 65, 0.9)'; ctx.fillStyle = 'rgba(226,80,65,0.12)'; }
    else { ctx.fillStyle = 'rgba(33,150,243,0.12)'; ctx.strokeStyle = 'rgba(33,150,243,0.7)'; }
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(cx, cy, rPxX, rPxY, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  }
  function clearPreview(){ const overlay = overlayRef.current; if(!overlay) return; const ctx = overlay.getContext('2d'); if(!ctx) return; ctx.clearRect(0,0,overlay.width, overlay.height); }

  function applyMoveSelection(dx:number, dy:number){
    if(!selection) return;
    // delegate to new move-from-selection function using current selection
    applyMoveSelectionFrom(selection, dx, dy);
  }

  function applyMoveSelectionFrom(sel:Sel, dx:number, dy:number){
    const x1 = Math.min(sel.x1, sel.x2), y1 = Math.min(sel.y1, sel.y2);
    const x2 = Math.max(sel.x1, sel.x2), y2 = Math.max(sel.y1, sel.y2);
    const w = x2 - x1 + 1, h = y2 - y1 + 1;
    const cells:Cell[] = [];
    for(let x=0;x<w;x++) for(let y=0;y<h;y++){ const v = filled.get(keyFor(x1 + x, y1 + y)); if(v) cells.push({x,y,color:v}); }
    // compute destination bounds
    const destX1 = x1 + dx, destY1 = y1 + dy;
    const destX2 = destX1 + w - 1, destY2 = destY1 + h - 1;
    applyFilledChange(m=>{
      // clear destination area first so moved cells replace whatever was there
      for(let x=destX1;x<=destX2;x++) for(let y=destY1;y<=destY2;y++) m.delete(keyFor(x,y));
      // clear source area
      for(let x=x1;x<=x2;x++) for(let y=y1;y<=y2;y++) m.delete(keyFor(x,y));
      // place cells at destination
      for(const c of cells) m.set(keyFor(destX1 + c.x, destY1 + c.y), c.color);
      ensurePaddingAroundFilled(m);
      return m;
    });
    // update selection to new location
    const newSel = { x1: destX1, y1: destY1, x2: destX2, y2: destY2 };
    setSelection(newSel);
  }

  // keyboard handlers
  useEffect(()=>{
    function onKey(e: KeyboardEvent){
      // Undo: Ctrl/Cmd + Z
      if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z'){
        e.preventDefault(); undo(); return;
      }
      if(e.key === 'Escape'){
        // if in copy tool, switch back to pencil and clear floating preview
        if(tool === 'copy'){
          setTool('pencil');
          if(copyPreview) setCopyPreview(null);
          return;
        }
        if(copyPreview){ setCopyPreview(null); return; }
        setSelection(null);
        return;
      }
      // If the copy tool is active and a floating preview exists, allow quick transforms
      if(tool === 'copy' && copyPreview){
        const k = e.key.toLowerCase();
        if(k === 'f'){
          e.preventDefault();
          // flip horizontally across preview width
          let maxX = -Infinity, maxY = -Infinity;
          for(const c of copyPreview){ if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
          if(maxX === -Infinity) maxX = 0; if(maxY === -Infinity) maxY = 0;
          const flipped = copyPreview.map(c => ({ x: (maxX - c.x), y: c.y, color: c.color }));
          setCopyPreview(flipped);
          return;
        }
        if(k === 'r'){
          e.preventDefault();
          // rotate 90deg clockwise around origin of preview (0..max)
          let maxX = -Infinity, maxY = -Infinity;
          for(const c of copyPreview){ if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
          if(maxX === -Infinity) maxX = 0; if(maxY === -Infinity) maxY = 0;
          const rotated = copyPreview.map(c => ({ x: (maxY - c.y), y: c.x, color: c.color }));
          setCopyPreview(rotated);
          return;
        }
      }
      // Select all filled cells: Ctrl/Cmd + A
      if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a'){
        e.preventDefault();
        // switch to select tool so selection UI is active
        setTool('select');
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        filled.forEach((v,k)=>{ const [x,y] = k.split(',').map(s=>parseInt(s,10)); if(x<minX) minX = x; if(y<minY) minY = y; if(x>maxX) maxX = x; if(y>maxY) maxY = y; });
        if(minX === Infinity){ // nothing filled
          setSelection(null);
        } else {
          // if the computed bounding box is a single cell and the select tool is being used, treat as no selection
          if(minX === maxX && minY === maxY){
            setSelection(null);
          } else {
            setSelection({ x1: minX, y1: minY, x2: maxX, y2: maxY });
          }
        }
        return;
      }
      if(!selection) {
        // global shortcuts
        if((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v'){
          // paste
          if(clipboard) {
            // paste at center
            const ox = startX + Math.floor(cols/2);
            const oy = startY + Math.floor(rows/2);
            applyFilledChange(m=>{ for(const c of clipboard) m.set(keyFor(ox + c.x, oy + c.y), c.color); ensurePaddingAroundFilled(m); return m; });
            // select the pasted area
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for(const c of clipboard){ if(c.x < minX) minX = c.x; if(c.y < minY) minY = c.y; if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
            if(minX !== Infinity){ setSelection({ x1: ox + minX, y1: oy + minY, x2: ox + maxX, y2: oy + maxY }); }
          }
        }
        return;
      }
      const dx = (e.key === 'ArrowRight') ? 1 : (e.key === 'ArrowLeft') ? -1 : 0;
      const dy = (e.key === 'ArrowDown') ? 1 : (e.key === 'ArrowUp') ? -1 : 0;
      if(dx || dy){
        e.preventDefault(); applyMoveSelection(dx,dy); return;
      }
      if(e.key.toLowerCase() === 'c' && (e.ctrlKey || e.metaKey)){
        // copy
        const x1 = Math.min(selection.x1, selection.x2), y1 = Math.min(selection.y1, selection.y2);
        const x2 = Math.max(selection.x1, selection.x2), y2 = Math.max(selection.y1, selection.y2);
        const cells:Cell[] = [];
        for(let x=x1;x<=x2;x++) for(let y=y1;y<=y2;y++){ const v = filled.get(keyFor(x,y)); if(v) cells.push({x:x-x1,y:y-y1,color:v}); }
        setClipboard(cells.length ? cells : null);
      }
      if(e.key.toLowerCase() === 'v' && (e.ctrlKey || e.metaKey)){
        // paste into selection origin
        if(clipboard){
          const ox = Math.min(selection.x1,selection.x2); const oy = Math.min(selection.y1,selection.y2);
          applyFilledChange(m=>{ for(const c of clipboard) m.set(keyFor(ox + c.x, oy + c.y), c.color); ensurePaddingAroundFilled(m); return m; });
          // update selection to the pasted area
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for(const c of clipboard){ if(c.x < minX) minX = c.x; if(c.y < minY) minY = c.y; if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
          if(minX !== Infinity){ setSelection({ x1: ox + minX, y1: oy + minY, x2: ox + maxX, y2: oy + maxY }); }
        }
      }
      if(e.key === 'Delete'){
        // delete selection
        const x1 = Math.min(selection.x1, selection.x2), x2 = Math.max(selection.x1, selection.x2);
        const y1 = Math.min(selection.y1, selection.y2), y2 = Math.max(selection.y1, selection.y2);
        applyFilledChange(m=>{ for(let x=x1;x<=x2;x++) for(let y=y1;y<=y2;y++) m.delete(keyFor(x,y)); ensurePaddingAroundFilled(m); return m; });
      }
      if(e.key.toLowerCase() === 'r'){
        // Only rotate when Ctrl/Cmd is NOT held — allow Ctrl+R to be reserved by browser/OS or other shortcuts
        if(!(e.ctrlKey || e.metaKey)){
          // rotate selection 90deg clockwise
          rotateSelection(true);
        }
      }
      if(e.key.toLowerCase() === 'm'){
        // mirror horizontally
        mirrorSelection();
      }
    }
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  },[selection,clipboard,filled,startX,startY,cols,rows,copyPreview]);

  // when entering pencil/brush/eraser, hide selection overlay (main canvas will not draw selection in those tools)
  useEffect(()=>{
    if(tool === 'pencil' || tool === 'brush' || tool === 'eraser'){
      clearOverlay();
    }
  },[tool]);

  // click-based selection expansion: double-click => 4-connected, triple-click => 8-connected
  useEffect(()=>{
    const canvas = canvasRef.current; if(!canvas) return;
    function onClick(e: MouseEvent){
      if(tool !== 'select') return;
      // detail indicates click count (1,2,3)
      const clicks = (e as any).detail || 1;
      if(clicks !== 2 && clicks !== 3) return;
      const pos = clientToCell(e.clientX, e.clientY); if(!pos) return;
      const k = keyFor(pos.col, pos.row);
      const colorId = filled.get(k);
      if(!colorId) return;
      const stack: [number,number][] = [[pos.col, pos.row]];
      const seen = new Set<string>();
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      const neighbors = clicks === 3 ? [[1,0],[-1,0],[0,1],[0,-1],[1,1],[1,-1],[-1,1],[-1,-1]] : [[1,0],[-1,0],[0,1],[0,-1]];
      while(stack.length){
        const [x,y] = stack.pop()!;
        const key = keyFor(x,y);
        if(seen.has(key)) continue;
        seen.add(key);
        const v = filled.get(key);
        if(v !== colorId) continue;
        if(x < minX) minX = x; if(y < minY) minY = y; if(x > maxX) maxX = x; if(y > maxY) maxY = y;
        for(const [dx,dy] of neighbors) stack.push([x+dx, y+dy]);
      }
      if(minX !== Infinity){ setSelection({ x1: minX, y1: minY, x2: maxX, y2: maxY }); }
    }
    canvas.addEventListener('click', onClick as any);
    return ()=> canvas.removeEventListener('click', onClick as any);
  },[tool, filled, startX, startY, cols, rows]);

  // update cursor style based on active tool, Shift key, and right-button panning
  useEffect(()=>{
    const canvas = canvasRef.current; const overlay = overlayRef.current; const hover = hoverOverlayRef.current;
    if(!canvas) return;
    const baseCursorForTool = (t: Tool) => {
      switch(t){
        case 'pencil': return 'crosshair';
        case 'brush': return 'crosshair';
        case 'eraser': return 'cell';
        case 'fill': return 'crosshair';
        case 'select': return 'default';
        case 'move': return 'move';
        case 'copy': return 'copy';
        case 'eyedropper': return 'crosshair';
        default: return 'default';
      }
    };
    const setAll = (c: string) => { try{ canvas.style.cursor = c; }catch(e){} if(overlay) try{ overlay.style.cursor = c; }catch(e){} if(hover) try{ hover.style.cursor = c; }catch(e){} };

    // build a simple SVG eyedropper cursor data URL
    let eyedropperCursor = '';
    try{
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path fill='%23000' d='M19.7 4.3c-.4-.4-1-.4-1.4 0l-1.6 1.6-2.1-2.1 1.6-1.6c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0l-1.6 1.6L7 6.3 3.7 9.6c-.4.4-.4 1 0 1.4l7.1 7.1 3.6-3.6 1.6-1.6c.4-.4.4-1 0-1.4L11.7 6.3l1.6-1.6L19.7 4.3z'/></svg>`;
      const uri = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
      eyedropperCursor = `url("${uri}") 12 12, crosshair`;
    }catch(e){ eyedropperCursor = 'crosshair'; }

    // apply initial cursor (use the eyedropper cursor if the eyedropper tool is active)
    if(tool === 'eyedropper') setAll(eyedropperCursor); else setAll(baseCursorForTool(tool));

    const onKeyDown = (e: KeyboardEvent) => {
      if(e.key === 'Shift'){
        // show eraser-like cursor when Shift held for applicable tools
        if(tool === 'pencil' || tool === 'brush' || tool === 'eraser') setAll('cell');
      }
      if(e.key === 'Control' || e.key === 'Meta'){
        // show eyedropper when Ctrl/Cmd held for painting tools
        if(tool === 'pencil' || tool === 'brush' || tool === 'fill') setAll(eyedropperCursor);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if(e.key === 'Shift') setAll(baseCursorForTool(tool));
      if(e.key === 'Control' || e.key === 'Meta') setAll(baseCursorForTool(tool));
    };

    const onMouseDown = (e: MouseEvent) => {
      if(e.button === 2){ // right button -> panning
        setAll('grabbing');
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      if(e.button === 2){ setAll(baseCursorForTool(tool)); }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);

    return ()=>{
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
    };
  },[tool]);

  // cancel copy preview if user switches away from copy tool
  useEffect(()=>{
    if(tool !== 'copy' && copyPreview) setCopyPreview(null);
  },[tool]);

  // wheel and mouse preview handlers: allow panning with wheel and show brush preview
  useEffect(()=>{
    const container = containerRef.current; const canvas = canvasRef.current; if(!container||!canvas) return;
    function onWheel(e:WheelEvent){
      // Ctrl/Cmd + wheel -> zoom centered on mouse
      if(e.ctrlKey || e.metaKey){
        e.preventDefault();
        const canvasEl = canvasRef.current; if(!canvasEl) return;
        const rect = canvasEl.getBoundingClientRect();
        const x = e.clientX - rect.left - NUMBER_MARGIN;
        const y = e.clientY - rect.top - NUMBER_MARGIN;
        const colBefore = Math.floor(x / cellW) + startX;
        const rowBefore = Math.floor(y / cellH) + startY;
        const factor = (e.deltaY < 0) ? 1.12 : 0.88;
        // allow more zoom-out (smaller cell size); minimum cell size lowered from 6 to 3
        let newSize = Math.max(3, Math.round(cellSize * factor));
        // if rounding produced no change at extremes, nudge by one to ensure zoom direction works
        if(newSize === cellSize){
          if(factor > 1) newSize = cellSize + 1;
          else newSize = Math.max(3, cellSize - 1);
        }
        // compute new start so the cell under mouse remains same (use the final newSize)
        const newCellW = (cellAspect === 'long') ? Math.round(newSize * 1.5) : newSize;
        const newCellH = (cellAspect === 'tall') ? Math.round(newSize * 1.5) : newSize;
        const colAfter = Math.floor(x / newCellW) + startX;
        const rowAfter = Math.floor(y / newCellH) + startY;
        const dx = colBefore - colAfter;
        const dy = rowBefore - rowAfter;
        setCellSize(newSize);
        if(dx) setStartX(s => s + dx);
        if(dy) setStartY(s => s + dy);
        return;
      }
      // If shift is held, use vertical wheel to pan horizontally (common UX)
      const step = (val:number) => Math.sign(val) * Math.max(1, Math.floor(Math.abs(val) / 16));
      if(e.shiftKey){
        const dx = step(e.deltaY);
        setStartX(s => s + dx);
      } else {
        // normal wheel pans vertically
        const dy = step(e.deltaY);
        setStartY(s => s + dy);
        // also respond to horizontal wheel for small horizontal pans
        if(Math.abs(e.deltaX) > 1){ const dx = step(e.deltaX); setStartX(s => s + dx); }
      }
      e.preventDefault();
    }
    // rAF throttling: keep latest mouse pos and schedule drawCopyPreview once per frame
    const rafRef = { id: null as number | null };
    const pendingMouse = { x: 0, y: 0 };
    const copyPreviewRef = { current: null as Cell[] | null };
    // sync copyPreviewRef when copyPreview changes (we'll update below via effect)

    function onMove(e: MouseEvent){
      if(tool === 'copy' && copyPreview){
        // store latest mouse pos and schedule an rAF
        pendingMouse.x = e.clientX; pendingMouse.y = e.clientY;
        copyPreviewRef.current = copyPreview;
        if(rafRef.id == null){
          rafRef.id = window.requestAnimationFrame(()=>{
            rafRef.id = null;
            const cells = copyPreviewRef.current;
            if(cells) drawCopyPreview(pendingMouse.x, pendingMouse.y, cells);
          });
        }
        return;
      } else if(tool === 'pencil' || tool === 'brush' || tool === 'eraser') drawBrushPreview(e.clientX, e.clientY, e.shiftKey);
      else if(!draggingRef.current){
        // if user has a persistent selection and is in select/move, keep showing it
        // transient overlay handling — persistent selection is drawn on main canvas
        clearPreview();
      }
    }
    function onLeave(){ clearPreview(); }
    container.addEventListener('wheel', onWheel, { passive: false } as any);
    canvas.addEventListener('mousemove', onMove as any);
    canvas.addEventListener('mouseleave', onLeave as any);
    return ()=>{
      container.removeEventListener('wheel', onWheel as any);
      canvas.removeEventListener('mousemove', onMove as any);
      canvas.removeEventListener('mouseleave', onLeave as any);
      if(rafRef.id != null) window.cancelAnimationFrame(rafRef.id);
    };
  },[tool,width,cellSize,cellAspect,startX,startY,copyPreview,copyPreviewSingleUse,colors]);

  // theme application and redraw on change
  useEffect(()=>{
    if(dark) document.documentElement.classList.add('dark'); else document.documentElement.classList.remove('dark');
    draw();
  },[dark]);

  // apply grid background var when changed
  // Note: do NOT modify document root CSS variables here — only redraw the editor using `gridBg`.

  function rotateSelection(clockwise:boolean){
    if(!selection) return;
    const x1 = Math.min(selection.x1, selection.x2), y1 = Math.min(selection.y1, selection.y2);
    const x2 = Math.max(selection.x1, selection.x2), y2 = Math.max(selection.y1, selection.y2);
    const w = x2 - x1 + 1, h = y2 - y1 + 1;
    const cells:Cell[] = [];
    for(let x=0;x<w;x++) for(let y=0;y<h;y++){ const v = filled.get(keyFor(x1 + x, y1 + y)); if(v) cells.push({x,y,color:v}); }
    // place rotated into same top-left
    applyFilledChange(m=>{
      // clear old
      for(let x=0;x<w;x++) for(let y=0;y<h;y++) m.delete(keyFor(x1 + x, y1 + y));
      // compute and set rotated
      for(const c of cells){
        let tx=0, ty=0;
        if(clockwise){ tx = x1 + (h - 1 - c.y); ty = y1 + c.x; }
        else { tx = x1 + c.y; ty = y1 + (w - 1 - c.x); }
        m.set(keyFor(tx,ty), c.color);
      }
      ensurePaddingAroundFilled(m);
      return m;
    });
    // update selection dims
    if(clockwise) setSelection({x1: x1, y1: y1, x2: x1 + h - 1, y2: y1 + w - 1});
    else setSelection({x1: x1, y1: y1, x2: x1 + h - 1, y2: y1 + w - 1});
  }

  function mirrorSelection(){
    if(!selection) return;
    const x1 = Math.min(selection.x1, selection.x2), y1 = Math.min(selection.y1, selection.y2);
    const x2 = Math.max(selection.x1, selection.x2), y2 = Math.max(selection.y1, selection.y2);
    const w = x2 - x1 + 1, h = y2 - y1 + 1;
    const cells:Cell[] = [];
    for(let x=0;x<w;x++) for(let y=0;y<h;y++){ const v = filled.get(keyFor(x1 + x, y1 + y)); if(v) cells.push({x,y,color:v}); }
    applyFilledChange(m=>{ for(let x=0;x<w;x++) for(let y=0;y<h;y++) m.delete(keyFor(x1 + x, y1 + y)); for(const c of cells){ const tx = x1 + (w - 1 - c.x); const ty = y1 + c.y; m.set(keyFor(tx,ty), c.color); } ensurePaddingAroundFilled(m); return m; });
  }

  function flipSelectionVertical(){
    if(!selection) return;
    const x1 = Math.min(selection.x1, selection.x2), y1 = Math.min(selection.y1, selection.y2);
    const x2 = Math.max(selection.x1, selection.x2), y2 = Math.max(selection.y1, selection.y2);
    const w = x2 - x1 + 1, h = y2 - y1 + 1;
    const cells:Cell[] = [];
    for(let x=0;x<w;x++) for(let y=0;y<h;y++){ const v = filled.get(keyFor(x1 + x, y1 + y)); if(v) cells.push({x,y,color:v}); }
    applyFilledChange(m=>{ for(let x=0;x<w;x++) for(let y=0;y<h;y++) m.delete(keyFor(x1 + x, y1 + y)); for(const c of cells){ const tx = x1 + c.x; const ty = y1 + (h - 1 - c.y); m.set(keyFor(tx,ty), c.color); } ensurePaddingAroundFilled(m); return m; });
  }

  function startCopyTool(){
    if(!selection) { setCopyPreview(null); return; }
    const x1 = Math.min(selection.x1, selection.x2), y1 = Math.min(selection.y1, selection.y2);
    const x2 = Math.max(selection.x1, selection.x2), y2 = Math.max(selection.y1, selection.y2);
    const cells:Cell[] = [];
    for(let x=x1;x<=x2;x++) for(let y=y1;y<=y2;y++){ const v = filled.get(keyFor(x,y)); if(v) cells.push({x: x - x1, y: y - y1, color: v}); }
    setCopyPreview(cells.length ? cells : null);
  }

  // handle image import modal results: create palette entries for unique hex colors and set a floating copyPreview using palette ids
  function handleImageImportDone(cells: ImportCell[]){
    if(!cells || cells.length === 0) return;
    // map existing hex -> id (case-insensitive)
    const hexToId = new Map<string,string>();
    for(const [id,def] of colors.entries()) hexToId.set(def.hex.toLowerCase(), id);
    const newColors = new Map(colors);
    const base = Date.now().toString(36);
    let counter = 0;
    // collect unique hexes from cells in order
    const uniqueHexes: string[] = [];
    for(const c of cells){ const h = c.color.toLowerCase(); if(!uniqueHexes.includes(h)) uniqueHexes.push(h); }
    for(const h of uniqueHexes){
      if(!hexToId.has(h)){
        const id = `c${base}${counter++}`;
        // map the imported hex to the nearest yarn color name
        const nearestName = findNearestYarnName(h);
        newColors.set(id, { name: nearestName, hex: h });
        hexToId.set(h, id);
      }
    }
    setColors(newColors);
    // build preview cells using palette ids
    const previewCells: Cell[] = cells.map(c=>({ x: c.x, y: c.y, color: hexToId.get(c.color.toLowerCase())! }));
    setCopyPreview(previewCells.length ? previewCells : null);
    // mark this preview as single-use (will clear after one paste)
    setCopyPreviewSingleUse(true);
    // open copy tool so user can paste once
    setTool('copy');
  }

  function exportCSV(){
    const cells:Cell[] = [];
    filled.forEach((colorId,k)=>{ const [x,y] = k.split(',').map(s=>parseInt(s,10)); const hex = colors.get(colorId)?.hex || '#000000'; cells.push({x,y,color:hex}); });
    const text = cellsToCSV(cells);
    const blob = new Blob([text],{type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'yarngrid.pattern'; a.click(); URL.revokeObjectURL(url);
  }

  async function importCSV(file: File){
    const txt = await file.text();
    const cells = csvToCells(txt);
    // Build map of existing hex -> id (normalized uppercase)
    const existingHexToId = new Map<string,string>();
    for(const [id, def] of colors.entries()) existingHexToId.set(def.hex.toUpperCase(), id);

    // Collect unique hexes from imported file
    const uniqueHexes: string[] = [];
    const seen = new Set<string>();
    for(const c of cells){ const h = (c.color || '').toUpperCase(); if(!seen.has(h)){ seen.add(h); uniqueHexes.push(h); } }

    // Prepare new colors map by cloning current colors once and adding any missing hexes
    const newColors = new Map<string,{name:string,hex:string}>(colors);
    const hexToId = new Map<string,string>();
    // seed existing mappings
    for(const [hex, id] of existingHexToId.entries()) hexToId.set(hex, id);

    // Add missing imported hexes once
    for(const hex of uniqueHexes){
      if(!hexToId.has(hex)){
        const id = `c${Date.now().toString(36)}${Math.floor(Math.random()*1000)}`;
        const name = findNearestYarnName(hex) || `Color ${newColors.size+1}`;
        newColors.set(id, { name, hex });
        hexToId.set(hex, id);
      }
    }

    // apply the new colors (single update)
    setColors(newColors);

    // build mapped preview cells (palette ids) and a replaced filled map
    const previewCells: Cell[] = cells.map(c=>({ x: c.x, y: c.y, color: hexToId.get((c.color||'').toUpperCase()) || existingHexToId.get((c.color||'').toUpperCase()) || 'c0' }));
    const next = new Map<string,string>();
    for(const p of previewCells) next.set(keyFor(p.x, p.y), p.color);

    // If there's existing content, open the in-app modal to ask whether to replace or add/place
    if(filled.size > 0){
      setLoadModalData({ preview: previewCells, next });
      return;
    } else {
      // no existing content — replace directly
      replaceFilled(next);
      showToast('Pattern loaded');
    }
  }

  async function exportImage(){
    const cells:Cell[] = [];
    filled.forEach((colorId,k)=>{ const [x,y] = k.split(',').map(s=>parseInt(s,10)); const hex = colors.get(colorId)?.hex || '#000000'; cells.push({x,y,color:hex}); });
    const exportCellSize = Math.max(4, Math.min(cellW, cellH) - 2);
    const data = await exportToImage(cells, exportCellSize, 2, gridBg, true, 3, false, undefined);
    const a = document.createElement('a'); a.href = data; a.download = 'knit.png'; a.click();
  }

  function handleTrim(){
    // compute bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    filled.forEach((v,k)=>{ const [x,y] = k.split(',').map(s=>parseInt(s,10)); if(x<minX)minX=x; if(y<minY)minY=y; if(x>maxX)maxX=x; if(y>maxY)maxY=y; });
    if(minX===Infinity) {
      // nothing filled; reset viewport
      setStartX(0); setStartY(0); setCols(50); setRows(50);
      return;
    }
    const contentW = maxX - minX + 1;
    const contentH = maxY - minY + 1;
    const newCols = contentW + 2; // keep one blank border
    const newRows = contentH + 2;
    const shiftX = 1 - minX; // translate so minX maps to 1
    const shiftY = 1 - minY;
    const newMap = new Map<string,string>();
    filled.forEach((v,k)=>{
      const [x,y] = k.split(',').map(s=>parseInt(s,10));
      newMap.set(keyFor(x + shiftX, y + shiftY), v);
    });
    replaceFilled(newMap);
    setStartX(0); setStartY(0); setCols(newCols); setRows(newRows);
  }

  // toolbar handlers extracted to named functions to avoid large inline jsx blocks
  function handleDeleteSelection(){
    if(!selection) return;
    const x1 = Math.min(selection.x1, selection.x2); const x2 = Math.max(selection.x1, selection.x2);
    const y1 = Math.min(selection.y1, selection.y2); const y2 = Math.max(selection.y1, selection.y2);
    applyFilledChange(m=>{ for(let x=x1;x<=x2;x++) for(let y=y1;y<=y2;y++) m.delete(keyFor(x,y)); ensurePaddingAroundFilled(m); return m; });
    setSelection(null);
  }

  function handleCopySelection(){
    if(!selection) return;
    const x1 = Math.min(selection.x1, selection.x2); const y1 = Math.min(selection.y1, selection.y2);
    const x2 = Math.max(selection.x1, selection.x2); const y2 = Math.max(selection.y1, selection.y2);
    const cells:Cell[] = [];
    for(let x=x1;x<=x2;x++) for(let y=y1;y<=y2;y++){ const v = filled.get(keyFor(x,y)); if(v) cells.push({x:x-x1,y:y-y1,color:v}); }
    setClipboard(cells.length ? cells : null);
  }

  function handlePasteClipboard(){
    if(!clipboard) return;
    const ox = startX + Math.floor(cols/2); const oy = startY + Math.floor(rows/2);
    applyFilledChange(m=>{ for(const c of clipboard) m.set(keyFor(ox + c.x, oy + c.y), c.color); ensurePaddingAroundFilled(m); return m; });
    // select pasted region
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for(const c of clipboard){ if(c.x < minX) minX = c.x; if(c.y < minY) minY = c.y; if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
    if(minX !== Infinity){ setSelection({ x1: ox + minX, y1: oy + minY, x2: ox + maxX, y2: oy + maxY }); }
  }

  return (
    <div className="grid-editor-root" style={{display:'flex',height:'100vh'}}>
      <div style={{width:220,flex:'0 0 220px'}}>
        <ColorPalette
          colors={Array.from(colors.entries()).map(([id,def])=>({id,name:def.name,hex:def.hex}))}
          selectedId={selectedColorId}
          setSelectedId={setSelectedColorId}
          addColor={addColor}
          updateColor={updateColor}
          removeColor={removeColor}
            gridBg={gridBg}
            setGridBg={setGridBg}
          onHoverColor={(id)=>setHoveredColorId(id || null)}
          dark={dark}
        />
      </div>

      <div style={{flex:1,display:'flex',flexDirection:'column'}}>
        <div style={{flex:'0 0 auto'}}>
          <Toolbar
            tool={tool} setTool={setTool}
            width={width} setWidth={setWidth}
            cellAspect={cellAspect} setCellAspect={(s) => setCellAspect(s)}
            onExportCSV={exportCSV}
            onImportCSV={importCSV}
            onExportImage={exportImage}
            onOpenExportOptions={() => setShowExportModal(true)}
            onDeleteSelection={handleDeleteSelection}
            onCopySelection={handleCopySelection}
            onPasteClipboard={handlePasteClipboard}
            onFlipHorizontal={mirrorSelection}
            onFlipVertical={flipSelectionVertical}
            onStartCopyTool={startCopyTool}
            onTrim={handleTrim}
            onOpenImageImport={() => setShowImageModal(true)}
            dark={dark}
            toggleDark={() => setDark(d => !d)}
            showGrid={showGrid}
            toggleShowGrid={() => setShowGrid(s => !s)}
          />
          <ImageImportModal open={showImageModal} onClose={() => setShowImageModal(false)} onDone={handleImageImportDone} onProcessingChange={(p)=>setProcessingGlobal(p)} />
          <ExportModal
            open={showExportModal}
            onClose={() => setShowExportModal(false)}
            cellSize={cellSize}
            totalWidthCells={totalWidthCells}
            totalHeightCells={totalHeightCells}
            onExport={async (opts) => {
              const scale = opts.scale; const includeNumbers = opts.includeNumbers; const mode = (opts as any).mode || 'single';
              // build cells array from filled
              const allCells: Cell[] = [];
              for (const [k, v] of filled.entries()) { const [x, y] = k.split(',').map(s => parseInt(s, 10)); allCells.push({ x, y, color: colors.get(v)?.hex || '#000000' }); }
              if (mode === 'single'){
                try{
                  setProcessingGlobal(true);
                  const data = await exportToImage(allCells, Math.max(4, cellSize-2), 2, gridBg, includeNumbers, scale, false, undefined);
                  const a = document.createElement('a'); a.href = data; a.download = `knitgrid.png`; document.body.appendChild(a); a.click(); a.remove();
                  showToast('Export complete');
                }catch(err){ showToast('Export failed'); }
                finally{ setProcessingGlobal(false); }
                return;
              }
              // PDF export path
              const hSlices = (opts as any).hSlices || 1; const vSlices = (opts as any).vSlices || 1;
              // compute content bounds from filled cells
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              for(const c of allCells){ if(c.x < minX) minX = c.x; if(c.y < minY) minY = c.y; if(c.x > maxX) maxX = c.x; if(c.y > maxY) maxY = c.y; }
              if(minX === Infinity){ // nothing filled -> use viewport
                minX = startX; minY = startY; maxX = startX + cols - 1; maxY = startY + rows - 1;
              }
              const totalW = maxX - minX + 1; const totalH = maxY - minY + 1;
              const sliceW = Math.max(1, Math.ceil(totalW / hSlices));
              const sliceH = Math.max(1, Math.ceil(totalH / vSlices));
              try{
                setProcessingGlobal(true);
                const includeBOM = !!(opts as any).includeBillOfMaterials;
                const includeColorIndexInCells = !!(opts as any).includeColorIndexInCells;
                // compute global color counts and index mapping (BOM order: most-used first)
                const counts = new Map<string, number>();
                for (const c of allCells) { const hex = (c.color || '#000000').toUpperCase(); counts.set(hex, (counts.get(hex) || 0) + 1); }
                const entries = Array.from(counts.entries()).sort((a,b)=>b[1]-a[1]);
                const colorIndexMap: Record<string, number> = {};
                entries.forEach(([hex], i)=>{ colorIndexMap[hex] = i+1; });
                const pdfDoc = await PDFDocument.create();
                let pageWidthForBOM: number | null = null;
                // iterate slices top-to-bottom (y), left-to-right (x)
                for(let vy=0; vy<vSlices; vy++){
                  for(let hx=0; hx<hSlices; hx++){
                    const sx = minX + hx * sliceW; const sy = minY + vy * sliceH;
                    const ex = Math.min(maxX, sx + sliceW - 1); const ey = Math.min(maxY, sy + sliceH - 1);
                    // collect cells inside this slice
                    const subset = allCells.filter(c => c.x >= sx && c.x <= ex && c.y >= sy && c.y <= ey).map(c => ({ x: c.x, y: c.y, color: c.color }));
                    // if no cells in slice, still create blank image (so layout consistent)
                    const imgDataUrl = await exportToImage(
                      subset,
                      Math.max(4, cellSize - 2),
                      2,
                      gridBg,
                      includeNumbers,
                      scale,
                      includeColorIndexInCells,
                      colorIndexMap,
                      sx,
                      sy,
                      sliceW,
                      sliceH
                    );
                    // embed png first so we know page dimensions
                    const pngBytes = await (await fetch(imgDataUrl)).arrayBuffer();
                    const pngImage = await pdfDoc.embedPng(pngBytes);
                    const pngW = pngImage.width; const pngH = pngImage.height;
                    if(!pageWidthForBOM) pageWidthForBOM = pngW;
                    // create a compact locator image showing position within full grid
                    // Make it small (not stretched across page) but preserve the full-grid aspect ratio.
                    const desiredLocatorMaxW = 260; // small width in pixels
                    const desiredLocatorMinW = 120;
                    const aspect = Math.max(0.25, Math.min(4, totalH / Math.max(1, totalW)));
                    let locatorW = Math.min(desiredLocatorMaxW, Math.max(desiredLocatorMinW, Math.round(Math.min(desiredLocatorMaxW, Math.max(100, pngW * 0.18)))));
                    let locatorH = Math.max(36, Math.round(locatorW * aspect));
                    // cap locatorH to reasonable max
                    locatorH = Math.min(locatorH, 320);
                    const locCanvas = document.createElement('canvas'); locCanvas.width = locatorW; locCanvas.height = locatorH;
                    const lctx = locCanvas.getContext('2d')!;
                    // background and border
                    lctx.fillStyle = '#ffffff'; lctx.fillRect(0,0,locatorW,locatorH);
                    const pad = 6;
                    const innerW = locatorW - pad*2;
                    const innerH = locatorH - pad*2;
                    lctx.strokeStyle = '#ccc'; lctx.lineWidth = 1; lctx.strokeRect(pad, pad, innerW, innerH);
                    // draw gridlines representing how the grid is sliced into hSlices x vSlices
                    lctx.strokeStyle = 'rgba(0,0,0,0.12)'; lctx.lineWidth = 1;
                    for(let i=1;i<hSlices;i++){
                      const gx = pad + Math.round((i / hSlices) * innerW);
                      lctx.beginPath(); lctx.moveTo(gx + 0.5, pad); lctx.lineTo(gx + 0.5, pad + innerH); lctx.stroke();
                    }
                    for(let j=1;j<vSlices;j++){
                      const gy = pad + Math.round((j / vSlices) * innerH);
                      lctx.beginPath(); lctx.moveTo(pad, gy + 0.5); lctx.lineTo(pad + innerW, gy + 0.5); lctx.stroke();
                    }
                    // compute highlighted rect for this slice (both x and y)
                    const relX = (sx - minX) / Math.max(1, totalW);
                    const relY = (sy - minY) / Math.max(1, totalH);
                    const relW = (ex - sx + 1) / Math.max(1, totalW);
                    const relH = (ey - sy + 1) / Math.max(1, totalH);
                    const rectX = pad + relX * innerW;
                    const rectY = pad + relY * innerH;
                    const rectW = Math.max(2, relW * innerW);
                    const rectH = Math.max(2, relH * innerH);
                    lctx.fillStyle = 'rgba(33,150,243,0.22)'; lctx.fillRect(rectX, rectY, rectW, rectH);
                    lctx.strokeStyle = 'rgba(33,150,243,0.7)'; lctx.lineWidth = 1.5; lctx.strokeRect(rectX, rectY, rectW, rectH);
                    // small label inside/below locator
                    lctx.fillStyle = '#111'; lctx.font = '11px sans-serif';
                    const label = `Tile ${hx+1},${vy+1}`;
                    // try to draw label centered beneath locator if space
                    if(locatorH >= 40){
                      lctx.textAlign = 'center'; lctx.fillText(label, locatorW/2, locatorH - 8);
                    } else { lctx.textAlign = 'left'; lctx.fillText(label, pad+4, pad+12); }

                    // embed locator into PDF and compose page
                    const locBytes = await (await fetch(locCanvas.toDataURL())).arrayBuffer();
                    const locImg = await pdfDoc.embedPng(locBytes);
                    const margin = 8;
                    const pageH = pngH + locatorH + margin*2;
                    const page = pdfDoc.addPage([pngW, pageH]);
                    // draw png at bottom with margin
                    page.drawImage(pngImage, { x: 0, y: margin, width: pngW, height: pngH });
                    // draw locator centered above the png
                    const locX = Math.round((pngW - locatorW) / 2);
                    const locY = pngH + margin;
                    page.drawImage(locImg, { x: locX, y: locY, width: locatorW, height: locatorH });
                  }
                }
                // If BOM requested, render a BOM page and insert it at the front
                if(includeBOM){
                  try{
                    const pw = pageWidthForBOM || 612;
                    // compact centered table layout
                    const leftPad = 24;
                    const rightPad = 24;
                    const colIndexW = 28;
                    const swatchW = 18;
                    const gapAfterSwatch = 8;
                    const countW = 80;
                    // compute a reasonable table width (don't stretch full page)
                    const maxTableWidth = Math.min(520, pw - leftPad - rightPad);
                    const tableWidth = Math.max(220, maxTableWidth);
                    const tableLeft = Math.round((pw - tableWidth) / 2);
                    const nameW = tableWidth - colIndexW - swatchW - countW - gapAfterSwatch - 16;
                    const lineH = 20;
                    const titleH = 42;
                    const bomHeight = Math.max(110, titleH + entries.length * lineH + 20);
                    const bomCanvas = document.createElement('canvas'); bomCanvas.width = pw; bomCanvas.height = bomHeight;
                    const bctx = bomCanvas.getContext('2d')!;
                    // background
                    bctx.fillStyle = '#ffffff'; bctx.fillRect(0,0,pw,bomHeight);
                    // title
                    bctx.fillStyle = '#111'; bctx.font = '18px sans-serif'; bctx.textAlign = 'center'; bctx.fillText('Bill of Materials', pw/2, 26);
                    // header row (compact)
                    const headerY = titleH;
                    bctx.font = '12px sans-serif'; bctx.fillStyle = '#333'; bctx.textAlign = 'left';
                    bctx.fillText('#', tableLeft + 8, headerY);
                    bctx.fillText('Color', tableLeft + 8 + colIndexW, headerY);
                    bctx.fillText('Name', tableLeft + 16 + colIndexW + swatchW + gapAfterSwatch, headerY);
                    bctx.textAlign = 'right'; bctx.fillText('Count', tableLeft + tableWidth - 8, headerY);
                    // subtle separator
                    bctx.strokeStyle = 'rgba(0,0,0,0.07)'; bctx.lineWidth = 1; bctx.beginPath(); bctx.moveTo(tableLeft + 4, headerY + 6); bctx.lineTo(tableLeft + tableWidth - 4, headerY + 6); bctx.stroke();
                    // draw entries tightly packed
                    let y = headerY + 8 + 6;
                    bctx.font = '13px sans-serif';
                    for(const [hex, cnt] of entries){
                      const idx = colorIndexMap[hex] || '-';
                      // index
                      bctx.fillStyle = '#666'; bctx.textAlign = 'left'; bctx.fillText(String(idx), tableLeft + 8, y + 10);
                      // swatch
                      const swX = tableLeft + 8 + colIndexW;
                      bctx.fillStyle = hex; bctx.fillRect(swX, y + 2, swatchW, swatchW);
                      bctx.strokeStyle = 'rgba(0,0,0,0.06)'; bctx.strokeRect(swX, y + 2, swatchW, swatchW);
                      // name
                      let name = hex;
                      for(const [id, def] of colors.entries()){ if((def.hex||'').toUpperCase() === hex){ name = def.name; break; } }
                      bctx.fillStyle = '#111'; bctx.textAlign = 'left'; bctx.fillText(name, tableLeft + 16 + colIndexW + swatchW + gapAfterSwatch, y + 12, nameW);
                      // count (right aligned)
                      bctx.fillStyle = '#666'; bctx.textAlign = 'right'; bctx.fillText(String(cnt), tableLeft + tableWidth - 8, y + 12);
                      y += lineH;
                    }
                    const bomBytes = await (await fetch(bomCanvas.toDataURL())).arrayBuffer();
                    const bomImg = await pdfDoc.embedPng(bomBytes);
                    const bomPage = pdfDoc.insertPage(0, [pw, bomHeight]);
                    bomPage.drawImage(bomImg, { x: 0, y: 0, width: pw, height: bomHeight });
                  }catch(e){ console.error('Failed to render BOM page', e); }
                }
                const pdfBytes = await pdfDoc.save();
                const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'knitgrid.pdf'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                showToast('PDF export complete');
              }catch(err){ console.error(err); showToast('PDF export failed'); }
              finally{ setProcessingGlobal(false); }
            }}
          />
        </div>
        <div ref={containerRef} className="grid-container" style={{flex:1,display:'block',position:'relative',overflow:'hidden'}}>
          <canvas ref={canvasRef} style={{display:'block', width:'100%', height:'100%'}} />
          <canvas ref={overlayRef} style={{position:'absolute', left:0,top:0,pointerEvents:'none'}} />
          <canvas ref={hoverOverlayRef} style={{position:'absolute', left:0,top:0,pointerEvents:'none'}} />
          {/* Global processing indicator (fixed) and toast list */}
          {processingGlobal && (
            <div style={{position:'fixed', right:16, top:16, zIndex:2000, display:'flex', alignItems:'center', gap:8, background:'rgba(0,0,0,0.7)', color:'#fff', padding:'8px 10px', borderRadius:8}}>
              <svg width={18} height={18} viewBox="0 0 50 50" aria-hidden="true"><circle cx="25" cy="25" r="20" stroke="#fff" strokeWidth="4" fill="none" strokeDasharray="31.4 31.4"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite"/></circle></svg>
              <div style={{fontSize:13}}>Processing...</div>
            </div>
          )}
          {toasts.length > 0 && (
            <div style={{position:'fixed', right:16, top:64, zIndex:2000, display:'flex', flexDirection:'column', gap:8}}>
              {toasts.map(t => (
                <div key={t.id} style={{background:'rgba(0,0,0,0.8)', color:'#fff', padding:'8px 12px', borderRadius:6, minWidth:140, boxShadow:'0 2px 8px rgba(0,0,0,0.25)'}}>{t.text}</div>
              ))}
            </div>
          )}
          {/* Load decision modal (Replace vs Add & Place) */}
          {loadModalData && (
            <div style={{position:'fixed', left:0, top:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center'}}>
              <div role="dialog" aria-modal="true" style={{width:440, maxWidth:'94%', background:'#fff', padding:16, borderRadius:8, boxShadow:'0 12px 40px rgba(0,0,0,0.35)'}}>
                <h3 style={{margin:'0 0 8px 0'}}>Load Pattern</h3>
                <div style={{color:'#333', marginBottom:12}}>Grid already has content. Replace the existing drawing, or add and place the loaded data?</div>
                <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                  <button onClick={handleModalCancel} style={{background:'transparent',border:'1px solid #ccc',padding:'6px 10px',borderRadius:6}}>Cancel</button>
                  <button onClick={handleModalAddPlace} style={{background:'#1976d2',color:'#fff',border:'none',padding:'6px 10px',borderRadius:6}}>Add & Place</button>
                  <button onClick={handleModalReplace} style={{background:'#ef5350',color:'#fff',border:'none',padding:'6px 10px',borderRadius:6}}>Replace</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
